import { formatDistanceToNow } from "date-fns"

import type { LinearDateResolution } from "@productlane/db"

// Deadlines are date-only values stored as UTC midnight. Read UTC components
// everywhere so the calendar day never shifts in the viewer's local timezone.
function isLastDayOfMonth(date: Date): boolean {
	const nextDay = new Date(date)
	nextDay.setUTCDate(date.getUTCDate() + 1)
	return nextDay.getUTCMonth() !== date.getUTCMonth()
}

function isLastDayOfQuarter(date: Date): boolean {
	const month = date.getUTCMonth()
	const quarterEndMonths = [2, 5, 8, 11]
	return quarterEndMonths.includes(month) && isLastDayOfMonth(date)
}

function isLastDayOfYear(date: Date): boolean {
	return date.getUTCMonth() === 11 && date.getUTCDate() === 31
}

function isLastDayOfHalfYear(date: Date): boolean {
	const month = date.getUTCMonth()
	return (month === 5 || month === 11) && isLastDayOfMonth(date)
}

export function inferDateResolution(date: Date): LinearDateResolution | null {
	if (isLastDayOfYear(date)) {
		return "year"
	}
	if (isLastDayOfQuarter(date)) {
		return "quarter"
	}
	if (isLastDayOfHalfYear(date)) {
		return "halfYear"
	}
	if (isLastDayOfMonth(date)) {
		return "month"
	}
	return null
}

// Formats a date by its resolution for display.
// Pass `long: true` for tooltips (always shows year, full weekday for exact dates).
export function formatDateByResolution(
	date: Date,
	resolution?: string | null,
	{ long = false }: { long?: boolean } = {},
): string {
	const currentYear = new Date().getUTCFullYear()
	const sameYear = currentYear === date.getUTCFullYear()
	const quarter = Math.ceil((date.getUTCMonth() + 1) / 3)
	const half = date.getUTCMonth() < 6 ? "H1" : "H2"

	switch (resolution) {
		case "quarter":
			return `Q${quarter} ${date.getUTCFullYear()}`
		case "month":
			return date.toLocaleDateString("en-US", {
				timeZone: "UTC",
				month: !long && sameYear ? "long" : long ? "long" : "short",
				year: !long && sameYear ? undefined : "numeric",
			})
		case "halfYear":
			return !long && sameYear ? half : `${half} ${date.getUTCFullYear()}`
		case "year":
			return `${date.getUTCFullYear()}`
		default:
			return long
				? date.toLocaleDateString("en-US", {
						timeZone: "UTC",
						weekday: "long",
						year: "numeric",
						month: "long",
						day: "numeric",
					})
				: date.toLocaleDateString("en-US", {
						timeZone: "UTC",
						month: "short",
						day: "numeric",
					})
	}
}

// Relative wrapper: "in Q2 2026", "March ago", "2 months ago", etc.
export function getFormattedDueDate(
	date: Date,
	resolution: LinearDateResolution | null,
): string {
	if (resolution === null) {
		return formatDistanceToNow(date)
	}

	const formatted = formatDateByResolution(date, resolution)
	const isPast = date < new Date()
	return isPast ? `${formatted} ago` : `in ${formatted}`
}
